import { useEffect, useState } from 'react';

// Best-effort screenshot deterrent. Gated by the same admin flag as the
// dev-tools block. The browser cannot truly prevent OS-level screenshots
// (PrtScn, Snipping Tool, Cmd+Shift+3/4, phone screenshots), but it can:
//   1. Cover the page with a white "No screenshots allowed" overlay
//      whenever the window loses focus or becomes hidden — most screenshot
//      tools dim or hide the browser before capturing, so they end up
//      capturing the overlay.
//   2. Briefly show the overlay on PrintScreen / common screenshot key
//      combos for the case where the browser still has focus.
// When the flag is off (admin allowed dev tools) the hook is a no-op.
// Desktop-only: tablets and phones (width < 1024px) bypass this entirely —
// the focus-loss overlay is too disruptive on touch devices where the
// browser blurs constantly (keyboard, sheets, app switching).
export default function useScreenshotBlock(enabled) {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= 1024,
  );

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!enabled || !isDesktop) return undefined;

    const OVERLAY_ID = 'screenshot-block-overlay';

    function ensureOverlay() {
      let el = document.getElementById(OVERLAY_ID);
      if (el) return el;
      el = document.createElement('div');
      el.id = OVERLAY_ID;
      el.textContent = 'No screenshots allowed';
      document.body.appendChild(el);
      return el;
    }

    function show() {
      const el = ensureOverlay();
      el.classList.add('visible');
    }

    function hide() {
      const el = document.getElementById(OVERLAY_ID);
      if (el) el.classList.remove('visible');
    }

    // Flash the overlay for ~2.5s — used for PrintScreen and combo keys
    // where focus stays on the page so visibilitychange/blur won't fire.
    let flashTimer = null;
    function flash() {
      show();
      if (flashTimer) clearTimeout(flashTimer);
      flashTimer = setTimeout(() => {
        hide();
        flashTimer = null;
      }, 2500);
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') show();
      else hide();
    };
    const onBlur = () => show();
    const onFocus = () => hide();

    const onKeyDown = (e) => {
      const k = (e.key || '').toLowerCase();
      // Windows: PrintScreen, Win+PrintScreen, Alt+PrintScreen, Win+Shift+S
      // macOS:   Cmd+Shift+3 / 4 / 5 / 6
      // Most browsers don't see PrintScreen at all on Windows (the OS
      // swallows it), but a few do — covering it is cheap.
      if (
        e.key === 'PrintScreen' ||
        k === 'printscreen' ||
        (e.metaKey && e.shiftKey && (k === 's')) ||
        (e.metaKey && e.shiftKey && (k === '3' || k === '4' || k === '5' || k === '6'))
      ) {
        flash();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    document.addEventListener('keydown', onKeyDown);
    // Also catch PrintScreen on keyup — some browsers only fire keyup
    // because the OS intercepts keydown.
    document.addEventListener('keyup', onKeyDown);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyDown);
      if (flashTimer) clearTimeout(flashTimer);
      const el = document.getElementById(OVERLAY_ID);
      if (el) el.remove();
    };
  }, [enabled, isDesktop]);
}
